import { audio, soundOn } from "../game/sfx";

/**
 * سه صدای ساختنیِ منچ — زدن، زده‌شدن، و فانفارِ رسیدن — با Web Audio و بی فایل.
 *
 * زنگِ ورود به راهرو و ووشِ بیرون‌آمدن همان synth("chime") و synth("whoosh") ِ sfx.ts اند.
 * زمینه‌ی صدا همان audio() ِ sfx.ts است، نه زمینه‌ی جدا: زمینه‌ای که وسطِ اولین زدن (بیرونِ
 * لمسِ آدم) ساخته شود در سافاری/iOS خوابیده به دنیا می‌آید و اولین صدای هر نشست گم می‌شد؛
 * کلیدِ «صدا» ی سربرگ هم فقط زمینه‌ی sfx را بیدار می‌کرد.
 *
 * همان قاعده‌ی sfx: صدای خاموش، مرورگرِ بی‌اجازه (پیش از اولین لمس) یا بی‌پشتیبانی = هیچ؛
 * هرگز خطا نمی‌دهد و صدای انباشته را بعدِ اولین لمس یک‌جا پخش نمی‌کند.
 */
export type MenchSound =
  /** زدنِ مهره‌ی دیگری (هم‌تای SoundFx.Special ِ اپ): ضربه‌ی کوتاه و «پاپ». */
  | "hit"
  /** مهره‌ی خودت زده شد (هم‌تای SoundFx.BigPenalty): ضربه‌ی سنگین و نتِ افتان. */
  | "hurt"
  /** مهره‌ات به مقصد رسید: سه نتِ بالارونده و نتِ آخرِ کشیده. */
  | "fanfare";

export function menchSound(name: MenchSound, opts: { volume?: number } = {}): void {
  if (!soundOn.value) return;
  const c = audio();
  if (!c || c.state !== "running") return;
  try { play(c, name, opts.volume ?? 1); } catch { /* گرهِ ناپشتیبان — بی‌صدا */ }
}

function play(c: AudioContext, name: MenchSound, vol: number): void {
  const t0 = c.currentTime + 0.005;
  const out = c.createGain();
  out.gain.value = vol;
  out.connect(c.destination);
  const tone = (f0: number, f1: number, at: number, dur: number, peak: number, type: OscillatorType = "sine") => {
    const o = c.createOscillator(); o.type = type;
    o.frequency.setValueAtTime(f0, t0 + at);
    o.frequency.exponentialRampToValueAtTime(f1, t0 + at + dur);
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t0 + at);
    g.gain.exponentialRampToValueAtTime(peak, t0 + at + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + at + dur);
    o.connect(g).connect(out);
    o.start(t0 + at); o.stop(t0 + at + dur + 0.02);
  };
  const thump = (at: number, dur: number, peak: number, freq: number) => {
    // نویزِ کوتاهِ پایین‌گذر = برخوردِ مهره‌ی چوبی
    const b = c.createBuffer(1, Math.ceil(c.sampleRate * dur), c.sampleRate);
    const d = b.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length);
    const src = c.createBufferSource(); src.buffer = b;
    const lp = c.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = freq;
    const g = c.createGain();
    g.gain.setValueAtTime(peak, t0 + at);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + at + dur);
    src.connect(lp).connect(g).connect(out);
    src.start(t0 + at); src.stop(t0 + at + dur);
  };
  switch (name) {
    case "hit":
      thump(0, 0.16, 0.7, 1800);
      tone(190, 70, 0, 0.2, 0.5, "sine");
      tone(900, 420, 0.03, 0.14, 0.16, "triangle");
      break;
    case "hurt":
      thump(0, 0.24, 0.85, 900);
      tone(150, 48, 0, 0.3, 0.6, "sine");
      tone(440, 330, 0.12, 0.2, 0.14, "triangle");
      tone(330, 196, 0.3, 0.42, 0.14, "triangle");
      break;
    case "fanfare":
      // دو-می-سل و دوی بالا؛ نتِ آخر با پنجم زیرش کشیده می‌ماند
      tone(523, 523, 0, 0.14, 0.2, "triangle");
      tone(659, 659, 0.11, 0.14, 0.2, "triangle");
      tone(784, 784, 0.22, 0.14, 0.2, "triangle");
      tone(1047, 1047, 0.34, 0.55, 0.22, "triangle");
      tone(784, 784, 0.34, 0.55, 0.1, "sine");
      break;
  }
}
